import useEventSubscriber from '@/lib/hooks/useEventSubscriber.ts'
import { toast } from '@/lib/hooks/useToast.ts'
import { useUserStore } from '@/store/user.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import NotificationToastLink from '@/routes/projectStages/notifications/view/NotificationToastLink.tsx'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  getNotificationsByStageIdQuery,
  getUnreadNotificationsByStageIdQuery,
} from '@/routes/projectStages/notifications/lib/actions/notificationsActions.ts'
// import { queryClient } from '@/lib/api-client.ts'
import { SingleIssueEvent } from '@/routes/projectStages/issue/types/issue.ts'
import NotificationEventTypeString from '@/routes/projectStages/notifications/view/NotificationEventTypeString.tsx'

// Track recently shown toast event IRIs to prevent duplicates
// (React Query data can be stale, so we need local deduplication)
const TOAST_DEDUP_TIMEOUT_MS = 5000
const recentlyShownToasts = new Set<string>()

export default function useNotificationsSubscriber(
  projectUsers: ProjectStagesUsersT | null | undefined,
  stageId: string | number | null | undefined,
) {
  const user = useUserStore(state => state.user)
  const setAllUnreadNotificationsCount = useProjectStageStore(
    state => state.setAllUnreadNotificationsCount,
  )
  const updateNotificationsCountByIssueType = useProjectStageStore(
    state => state.updateNotificationsCountByIssueType,
  )

  const userIri = `/users/${user?.id}`
  const projectStageIri = `/project_stages/${stageId}`

  const { data: unreadNotifications } = useInfiniteQuery({
    ...getUnreadNotificationsByStageIdQuery(String(stageId)),
    enabled: !!stageId,
  })

  const { data: dataAll } = useInfiniteQuery({
    ...getNotificationsByStageIdQuery(String(stageId)),
    enabled: !!stageId,
  })

  const allUnread = unreadNotifications?.pages.flatMap(page => page?.notifications) ?? []
  const allRead = dataAll?.pages.flatMap(page => page?.notifications) ?? []
  const irisUnread = allUnread?.map(notification => notification?.event?.['@id']) ?? []
  const irisRead = allRead?.map(notification => notification?.event?.['@id']) ?? []

  function handleNotificationToast(event: SingleIssueEvent) {
    const eventIri = event[`@id`]
    if (!eventIri) return

    // Check local deduplication first (handles rapid duplicate events)
    if (recentlyShownToasts.has(eventIri)) {
      return
    }

    // Check React Query data (handles events already in the list)
    if (irisUnread.find(iri => eventIri === iri) || irisRead.find(iri => eventIri === iri)) {
      /*Notification events can be sent multiple times - for example if you open unread
       notification the same event is triggered - this check is to prevent that, we only want to
        show toast when the event is not added to notifications array yet */
      return
    }

    // Add to local dedup set and auto-remove after timeout
    recentlyShownToasts.add(eventIri)
    setTimeout(() => {
      recentlyShownToasts.delete(eventIri)
    }, TOAST_DEDUP_TIMEOUT_MS)

    toast({
      description: (
        <NotificationEventTypeString event={event} projectUsers={projectUsers} location={'toast'} />
      ),
      // @ts-expect-error api types are wrong
      title: event.issue?.name,
      // title: event.eventType,
      action: <NotificationToastLink event={event} projectStageIri={projectStageIri} />,
    })
  }

  useEventSubscriber(`${userIri}${projectStageIri}/notifications`, (e: MessageEvent) => {
    let eventData: Record<string, unknown>
    try {
      eventData = JSON.parse(e.data)
    } catch {
      console.error('[Notifications] Failed to parse SSE data')
      return
    }

    const event = (eventData.notification as Record<string, unknown>)?.event as
      | SingleIssueEvent
      | undefined

    const unreadCount = eventData.unreadCount as
      | { issueType?: string; total?: number; count?: number }
      | undefined

    const issueType = unreadCount?.issueType
    const unreadNotificationsTotal = unreadCount?.total
    const issueTypeCount = unreadCount?.count

    if (issueType !== undefined && issueTypeCount !== undefined) {
      updateNotificationsCountByIssueType(issueType, issueTypeCount)
    }
    if (unreadNotificationsTotal !== undefined) {
      setAllUnreadNotificationsCount(unreadNotificationsTotal)
    }

    if (!event) return
    handleNotificationToast(event)
  })
}
